import { z } from "zod";
import {
  CASH_LIMIT_MINOR_KEY as DOMAIN_CASH_LIMIT_KEY,
  DOC_TYPE,
  isComplianceThresholdMinor,
  KYC_THRESHOLD_MINOR_KEY as DOMAIN_KYC_THRESHOLD_KEY,
  TAX_RATE_BASIS_POINTS_KEY as DOMAIN_TAX_RATE_KEY,
  TAX_RATE_MAX_BASIS_POINTS,
  TAX_WARNING_KIND,
  VERIFICATION_STATUS,
} from "@goppy/domain";

/* ===========================================================================
 * US-E19.F4-0203-S02 (@UC9307 @UC9308 @UC9309 @UC9310 @UC9311) — COMPLIANCE:
 * chứng từ hợp lệ · KYC người thụ hưởng · sao kê công khai · cảnh báo thuế · cash-limit.
 * 🔴 @gate money + pii + config.
 *
 * (a) Tiền LUÔN là STRING minor-unit (B1 BigInt, no-float) — KHÔNG `z.number()` cho tiền.
 *     `tax_rate_basis_points` = INTEGER basis point (500 = 5%), KHÔNG float `0.05`.
 * (b) 🔴 PII: `PayeeKycBody` CHỈ có `payeeName` + `payeeRef` — **KHÔNG có field số CMND/CCCD/MST thô**.
 *     `payeeRef` là THAM CHIẾU/HASH do client tính. Schema `.strict()` ⇒ client lỡ gửi
 *     `nationalId`/`idNumber`/`taxCode` ⇒ **400 ngay ở biên**, số thô KHÔNG bao giờ chạm DB/log.
 * (c) 🔴 `TaxWarningDto.autoDeducted` = `z.literal(false)` — hệ thống KHÔNG tự khấu trừ/nộp thuế.
 *     Nếu ai đó lỡ trả `true`, parse FAIL (fail-loud) ⇒ không thể âm thầm trừ tiền người dùng.
 * (d) Enum docType/verificationStatus DERIVE từ `@goppy/domain` (1 nguồn sự thật với DB CHECK).
 * ========================================================================= */

const MONEY_INTEGER_STRING = z.string().regex(/^\d+$/, "Tiền phải là chuỗi số nguyên (minor unit).");

/* ─────────────────────────── (a) Chứng từ compliance ─────────────────────────── */

export const docTypeSchema = z.enum([
  DOC_TYPE.VAT_INVOICE,
  DOC_TYPE.RECEIPT,
  DOC_TYPE.CONTRACT,
  DOC_TYPE.OTHER,
]);
export type DocTypeDto = z.infer<typeof docTypeSchema>;

export const verificationStatusSchema = z.enum([
  VERIFICATION_STATUS.VALID,
  VERIFICATION_STATUS.INVALID,
  VERIFICATION_STATUS.UNVERIFIED,
]);
export type VerificationStatusDto = z.infer<typeof verificationStatusSchema>;

/**
 * Đính kèm chứng từ. `storageKey` = **KEY R2** (bền), KHÔNG phải URL presigned (URL có chữ ký +
 * hết hạn ⇒ lưu DB là sai — pattern H2 của 0040). Regex chặn `http(s)://` ngay ở biên.
 */
export const attachComplianceDocSchema = z
  .object({
    docType: docTypeSchema,
    storageKey: z
      .string()
      .trim()
      .min(1)
      .max(512)
      .refine((v) => !/^https?:\/\//i.test(v), {
        message: "storageKey phải là KEY lưu trữ, không phải URL (presigned URL không được lưu DB).",
      }),
    transactionId: z.string().uuid().optional(),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type AttachComplianceDocBody = z.infer<typeof attachComplianceDocSchema>;

export const complianceDocResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    transactionId: z.string().uuid().nullable(),
    docType: docTypeSchema,
    /** 🔴 Đính kèm mới LUÔN 'unverified' (evidence-là-claim). Không tự đánh 'valid'. */
    verificationStatus: verificationStatusSchema,
    storageKey: z.string(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type ComplianceDocResponse = z.infer<typeof complianceDocResponseSchema>;

/** Phân loại chứng từ (admin/treasurer soát) — CHỈ valid|invalid (không "tự động" hoá unverified). */
export const classifyComplianceDocSchema = z
  .object({
    verificationStatus: z.enum([VERIFICATION_STATUS.VALID, VERIFICATION_STATUS.INVALID]),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();
export type ClassifyComplianceDocBody = z.infer<typeof classifyComplianceDocSchema>;

/* ─────────────────────────── (b) KYC người thụ hưởng 🔴 PII ─────────────────────────── */

/**
 * 🔴 PII TỐI THIỂU. `payeeRef` = tham chiếu/hash (client hash số định danh trước khi gửi) —
 * hex/base64url, độ dài hạn chế. `.strict()` ⇒ mọi field lạ (`nationalId`, `idNumber`, `cccd`,
 * `taxCode`, `idPhotoUrl`…) bị **TỪ CHỐI Ở BIÊN** ⇒ số thô không bao giờ vào DB/log/audit.
 */
export const PAYEE_REF_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

export const payeeKycSchema = z
  .object({
    payeeName: z.string().trim().min(1).max(120),
    payeeRef: z
      .string()
      .trim()
      .regex(
        PAYEE_REF_PATTERN,
        "payeeRef phải là tham chiếu/hash (8–128 ký tự [A-Za-z0-9_-]) — KHÔNG gửi số định danh thô.",
      ),
    payeeMemberId: z.string().uuid().optional(),
  })
  .strict();
export type PayeeKycBody = z.infer<typeof payeeKycSchema>;

/** Response KYC — KHÔNG trả `payeeRef` ra ngoài (tham chiếu định danh, chỉ dùng nội bộ). */
export const payeeKycResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    payeeMemberId: z.string().uuid().nullable(),
    payeeName: z.string(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type PayeeKycResponse = z.infer<typeof payeeKycResponseSchema>;

/** 🔴 Mã lỗi 422 khi hoàn/chi ≥ ngưỡng mà THIẾU định danh người thụ hưởng (fail-closed, KHÔNG post). */
export const PAYEE_KYC_REQUIRED_CODE = "PAYEE_KYC_REQUIRED" as const;
export const PAYEE_KYC_REQUIRED_MESSAGE =
  "Khoản hoàn/chi này vượt ngưỡng bắt buộc định danh người thụ hưởng. Vui lòng lưu định danh trước khi thực hiện." as const;

/* ─────────────────────────── (c) Config compliance ─────────────────────────── */

export const KYC_THRESHOLD_MINOR_KEY = DOMAIN_KYC_THRESHOLD_KEY;
export const CASH_LIMIT_MINOR_KEY = DOMAIN_CASH_LIMIT_KEY;
export const TAX_RATE_BASIS_POINTS_KEY = DOMAIN_TAX_RATE_KEY;

export const COMPLIANCE_CONFIG_KEY_LIST = [
  KYC_THRESHOLD_MINOR_KEY,
  CASH_LIMIT_MINOR_KEY,
  TAX_RATE_BASIS_POINTS_KEY,
] as const;

/** Ngưỡng = chuỗi BigInt minor ≥ 0 (guard domain — no-float, KHÔNG z.number). */
export const complianceThresholdMinorPatchSchema = z
  .string()
  .refine((v) => isComplianceThresholdMinor(v), {
    message: "Ngưỡng phải là số nguyên không âm (đơn vị minor, dạng chuỗi).",
  });

/** Thuế suất = basis point INTEGER ∈ [0, 10000] — KHÔNG float. */
export const taxRateBasisPointsPatchSchema = z
  .number()
  .int()
  .min(0)
  .max(TAX_RATE_MAX_BASIS_POINTS);

/** Config compliance hiệu lực của 1 quỹ (fund > group > system). Tiền = string BigInt. */
export const complianceConfigSchema = z
  .object({
    kycThresholdMinor: complianceThresholdMinorPatchSchema,
    cashLimitMinor: complianceThresholdMinorPatchSchema,
    taxRateBasisPoints: taxRateBasisPointsPatchSchema,
  })
  .strict();
export type ComplianceConfigResponse = z.infer<typeof complianceConfigSchema>;

/* ─────────────────────────── (d) Cảnh báo thuế 🔴 KHÔNG KHẤU TRỪ ─────────────────────────── */

export const taxWarningKindSchema = z.enum([
  TAX_WARNING_KIND.PERSONAL_INCOME_INTEREST,
  TAX_WARNING_KIND.NONE,
]);

export const taxWarningShareSchema = z
  .object({
    memberId: z.string().uuid(),
    interestMinor: MONEY_INTEGER_STRING,
    taxEstimateMinor: MONEY_INTEGER_STRING,
  })
  .strict();

/**
 * 🔴 BẤT BIẾN Ở CONTRACT: `autoDeducted` = literal `false`. Không có cách nào trả `true` mà parse
 * qua được ⇒ "hệ thống tự khấu trừ thuế" là trạng thái KHÔNG BIỂU DIỄN ĐƯỢC (fail-loud).
 * `ledgerTxIds` KHÔNG tồn tại trong schema `.strict()` ⇒ nhánh cảnh báo thuế không thể trả bút toán.
 */
export const taxWarningResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    interestMinor: MONEY_INTEGER_STRING,
    taxEstimateMinor: MONEY_INTEGER_STRING,
    taxRateBasisPoints: z.number().int().min(0).max(TAX_RATE_MAX_BASIS_POINTS),
    warningKind: taxWarningKindSchema,
    autoDeducted: z.literal(false),
    /** Cờ cảnh báo tiền mặt lớn (≥ cash_limit_minor) — CẢNH BÁO, KHÔNG chặn. */
    cashLimitWarning: z.boolean(),
    cashLimitMinor: MONEY_INTEGER_STRING,
    shares: z.array(taxWarningShareSchema),
    currency: z.string().length(3),
  })
  .strict();
export type TaxWarningResponse = z.infer<typeof taxWarningResponseSchema>;

/** Body yêu cầu ước tính cảnh báo thuế cho lợi tức sắp chia. Tiền = string BigInt minor. */
export const taxWarningRequestSchema = z
  .object({
    interestMinor: MONEY_INTEGER_STRING,
  })
  .strict();
export type TaxWarningRequestBody = z.infer<typeof taxWarningRequestSchema>;

/* ─────────────────────────── (e) Sao kê công khai chuẩn ─────────────────────────── */

/**
 * Sao kê CÔNG KHAI (nhà tài trợ đọc qua transparency-link — KHÔNG auth). Redaction FAIL-CLOSED:
 * scope không bật `show_balance` ⇒ `redacted: true` và tổng = placeholder, KHÔNG phải "0"
 * (0 là NÓI DỐI về tiền — mirror REDACTED_PLACEHOLDER của E19.F3).
 *
 * 🔴 KHÔNG có field PII nào (không tên người, không email/SĐT, không memo free-text).
 */
export const PUBLIC_STATEMENT_REDACTED = "•••" as const;

const publicStatementVisibleSchema = z
  .object({
    redacted: z.literal(false),
    totalInMinor: MONEY_INTEGER_STRING,
    totalOutMinor: MONEY_INTEGER_STRING,
    balanceMinor: MONEY_INTEGER_STRING,
  })
  .strict();

const publicStatementRedactedSchema = z
  .object({
    redacted: z.literal(true),
    totalInMinor: z.literal(PUBLIC_STATEMENT_REDACTED),
    totalOutMinor: z.literal(PUBLIC_STATEMENT_REDACTED),
    balanceMinor: z.literal(PUBLIC_STATEMENT_REDACTED),
  })
  .strict();

export const publicStatementTotalsSchema = z.discriminatedUnion("redacted", [
  publicStatementVisibleSchema,
  publicStatementRedactedSchema,
]);

export const publicStatementSchema = z
  .object({
    groupName: z.string(),
    currency: z.string().length(3),
    /** Kỳ sao kê (ISO date-time, nửa mở [from, to)). */
    periodFrom: z.string().datetime(),
    periodTo: z.string().datetime(),
    totals: publicStatementTotalsSchema,
    /** Chứng từ đã đính kèm trong kỳ (ĐẾM theo trạng thái — evidence-là-claim, KHÔNG lộ key/PII). */
    documentCounts: z
      .object({
        valid: z.number().int().nonnegative(),
        invalid: z.number().int().nonnegative(),
        unverified: z.number().int().nonnegative(),
      })
      .strict(),
  })
  .strict();
export type PublicStatementResponse = z.infer<typeof publicStatementSchema>;
